import React, { useEffect, useState } from 'react';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { attendanceApi, payrollApi, PayrollItem } from '../../services/employeeService';
import { PageHeader, SectionCard, StatTile, EmptyState, LoadingState, StatusPill, PillTone } from '../../components/ui';
import { NoStaffProfileState } from './EmployeeTimePanel';
import { formatDuration, formatMoney, formatMonthYear } from '../../utils/format';

type Row = PayrollItem & { period?: { year: number; month: number; status: string } };

const PERIOD_STATUS: Record<string, { label: string; tone: PillTone }> = {
  DRAFT: { label: 'W przygotowaniu', tone: 'neutral' },
  REVIEW: { label: 'Do sprawdzenia', tone: 'warning' },
  APPROVED: { label: 'Zatwierdzona', tone: 'info' },
  PAID: { label: 'Wypłacona', tone: 'success' },
  CANCELLED: { label: 'Anulowana', tone: 'danger' },
};

const periodLabel = (p?: Row['period']) => (p ? formatMonthYear(new Date(p.year, p.month - 1, 1)) : '—');

const apiStatus = (e: unknown) => (e as { response?: { status?: number } })?.response?.status;

export const MyPayrollPage: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<'none' | 'noProfile' | 'failed'>('none');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const me = await attendanceApi.myStatus();
        setRows(await payrollApi.history(me.staff.id) as never);
        setLoadError('none');
      } catch (e) {
        setLoadError(apiStatus(e) === 404 ? 'noProfile' : 'failed');
      } finally { setLoading(false); }
    })();
  }, [attempt]);

  const latest = rows[0];
  const latestStatus = latest?.period ? PERIOD_STATUS[latest.period.status] : undefined;

  return (
    <Box>
      <PageHeader
        breadcrumbs={[{ label: 'Moje sprawy' }]}
        title="Moje wypłaty"
        description="Miesięczne rozliczenia udostępnione przez salon: czas pracy, kwota brutto i to, co trafia na konto."
      />

      {loading ? (
        <LoadingState variant="page" count={3} rows={3} />
      ) : loadError === 'noProfile' ? (
        <NoStaffProfileState lead="Wypłaty są rozliczane dla osób dodanych do zespołu salonu." />
      ) : loadError === 'failed' ? (
        <SectionCard>
          <EmptyState
            icon={<ErrorOutlineIcon />}
            title="Nie udało się wczytać rozliczeń"
            description="Sprawdź połączenie z internetem i spróbuj ponownie."
            actions={<Button variant="outlined" onClick={() => setAttempt((n) => n + 1)}>Spróbuj ponownie</Button>}
          />
        </SectionCard>
      ) : rows.length === 0 ? (
        <SectionCard>
          <EmptyState
            icon={<PaymentsOutlinedIcon />}
            title="Brak udostępnionych rozliczeń"
            description="Rozliczenie pojawi się tutaj, gdy salon przygotuje wypłatę za dany miesiąc."
          />
        </SectionCard>
      ) : (
        <>
          <Box sx={{ display: 'grid', gap: 2, mb: 2.5, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' }, '& > :first-of-type': { gridColumn: { xs: '1 / -1', sm: 'auto' } } }}>
            <StatTile
              tone="hero"
              label={`Na konto · ${periodLabel(latest.period)}`}
              value={formatMoney(latest.netPaid, { decimals: 2 })}
              hint={latestStatus ? latestStatus.label : 'Ostatnie rozliczenie'}
            />
            <StatTile label="Brutto" value={formatMoney(latest.grossSalary, { decimals: 2 })} hint={periodLabel(latest.period)} />
            <StatTile
              label="Czas rozliczeniowy"
              value={formatDuration(latest.billableWorkMinutes)}
              hint={`faktycznie ${formatDuration(latest.actualWorkMinutes)}`}
            />
          </Box>

          <SectionCard title="Historia rozliczeń" padding="none">
            <TableContainer>
              <Table sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Okres</TableCell>
                    <TableCell align="right">Czas faktyczny</TableCell>
                    <TableCell align="right">Czas rozliczeniowy</TableCell>
                    <TableCell align="right">Brutto</TableCell>
                    <TableCell align="right">Na konto</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const st = r.period ? PERIOD_STATUS[r.period.status] || { label: r.period.status, tone: 'neutral' as PillTone } : null;
                    return (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography component="span" sx={{ fontWeight: 600, fontSize: 'inherit' }}>{periodLabel(r.period)}</Typography>
                          {r.isCorrection && <StatusPill label="Korekta" tone="warning" size="small" noDot sx={{ ml: 1 }} />}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatDuration(r.actualWorkMinutes)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatDuration(r.billableWorkMinutes)}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatMoney(r.grossSalary, { decimals: 2 })}</TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', fontWeight: 700 }}>{formatMoney(r.netPaid, { decimals: 2 })}</TableCell>
                        <TableCell>{st ? <StatusPill label={st.label} tone={st.tone} /> : '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </SectionCard>
        </>
      )}
    </Box>
  );
};

export default MyPayrollPage;
